// ============================================================================
// VYVA Onboarding Portal - Global State Management (Zustand)
// ============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { 
  User, 
  Tenant, 
  SeniorFilters, 
  CallFilters, 
  NotificationPreferences 
} from '@/types';

// ============================================================================
// Auth Store
// ============================================================================
interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false, // Start as false - no loading state needed initially
      
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      login: (user) => set({ 
        user, 
        isAuthenticated: true, 
        isLoading: false 
      }),
      
      logout: () => set({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false 
      }),
      
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'vyva-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

// ============================================================================
// Tenant Store
// ============================================================================
interface TenantStore {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  
  // Actions
  setCurrentTenant: (tenant: Tenant) => void;
  setTenants: (tenants: Tenant[]) => void;
}

export const useTenantStore = create<TenantStore>()(
  persist(
    (set) => ({
      currentTenant: null,
      tenants: [],
      
      setCurrentTenant: (tenant) => set({ currentTenant: tenant }),
      setTenants: (tenants) => set({ tenants }),
    }),
    {
      name: 'vyva-tenant',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// ============================================================================
// UI Store
// ============================================================================
interface UIStore {
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  
  // Dialogs & Modals
  isAddUserDialogOpen: boolean;
  isExportDialogOpen: boolean;
  
  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setAddUserDialogOpen: (open: boolean) => void;
  setExportDialogOpen: (open: boolean) => void;
}

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: 'light',
      isAddUserDialogOpen: false,
      isExportDialogOpen: false,
      
      toggleSidebar: () => set((state) => ({ 
        sidebarCollapsed: !state.sidebarCollapsed 
      })),
      
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setTheme: (theme) => set({ theme }),
      setAddUserDialogOpen: (open) => set({ isAddUserDialogOpen: open }),
      setExportDialogOpen: (open) => set({ isExportDialogOpen: open }),
    }),
    {
      name: 'vyva-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);

// ============================================================================
// Filters Store
// ============================================================================
interface FiltersStore {
  seniorFilters: SeniorFilters;
  callFilters: CallFilters;
  
  // Actions
  setSeniorFilters: (filters: Partial<SeniorFilters>) => void;
  resetSeniorFilters: () => void;
  setCallFilters: (filters: Partial<CallFilters>) => void;
  resetCallFilters: () => void;
  resetAllFilters: () => void;
}

const defaultSeniorFilters: SeniorFilters = {
  search: '',
  status: 'all',
  language: 'all',
  hasCaregiver: 'all',
  module: 'all',
};

const defaultCallFilters: CallFilters = {
  search: '',
  status: 'all',
  priority: 'all',
  timeWindow: 'today',
};

export const useFiltersStore = create<FiltersStore>()((set) => ({
  seniorFilters: defaultSeniorFilters,
  callFilters: defaultCallFilters,
  
  setSeniorFilters: (filters) => set((state) => ({
    seniorFilters: { ...state.seniorFilters, ...filters }
  })),
  
  resetSeniorFilters: () => set({ seniorFilters: defaultSeniorFilters }),
  
  setCallFilters: (filters) => set((state) => ({
    callFilters: { ...state.callFilters, ...filters }
  })),
  
  resetCallFilters: () => set({ callFilters: defaultCallFilters }),
  
  resetAllFilters: () => set({
    seniorFilters: defaultSeniorFilters,
    callFilters: defaultCallFilters,
  }),
}));

// ============================================================================
// Notifications Store
// ============================================================================
interface NotificationsStore {
  preferences: NotificationPreferences;
  unreadCount: number;
  
  // Actions
  setPreferences: (prefs: Partial<NotificationPreferences>) => void;
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  clearUnread: () => void;
}

export const useNotificationsStore = create<NotificationsStore>()(
  persist(
    (set) => ({
      preferences: {
        onboardingAlerts: true,
        failedCalls: true,
        systemUpdates: false,
        dailyDigest: false,
        weeklyReport: true,
      },
      unreadCount: 0,
      
      setPreferences: (prefs) => set((state) => ({
        preferences: { ...state.preferences, ...prefs }
      })),
      
      setUnreadCount: (count) => set({ unreadCount: count }),
      incrementUnread: () => set((state) => ({ 
        unreadCount: state.unreadCount + 1 
      })),
      clearUnread: () => set({ unreadCount: 0 }),
    }),
    {
      name: 'vyva-notifications',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ preferences: state.preferences }),
    }
  )
);

// ============================================================================
// Bulk Import Store
// ============================================================================
interface BulkImportStore {
  file: File | null;
  previewData: unknown[];
  validRows: number;
  invalidRows: number;
  isProcessing: boolean;
  
  // Actions
  setFile: (file: File | null) => void;
  setPreviewData: (data: unknown[]) => void;
  setValidation: (valid: number, invalid: number) => void;
  setProcessing: (processing: boolean) => void;
  reset: () => void;
}

export const useBulkImportStore = create<BulkImportStore>()((set) => ({
  file: null,
  previewData: [],
  validRows: 0,
  invalidRows: 0,
  isProcessing: false,
  
  setFile: (file) => set({ file }),
  setPreviewData: (data) => set({ previewData: data }),
  setValidation: (valid, invalid) => set({ 
    validRows: valid, 
    invalidRows: invalid 
  }),
  setProcessing: (processing) => set({ isProcessing: processing }),
  reset: () => set({
    file: null,
    previewData: [],
    validRows: 0,
    invalidRows: 0,
    isProcessing: false,
  }),
}));

